import React, {
  ChangeEvent,
  KeyboardEvent,
  createRef,
  memo,
  useCallback,
  useEffect,
  useState,
} from 'react';

import { CSSTransition } from 'react-transition-group';

import { CloseIcon } from '../../assets/icons/CloseIcon';
import { PercentLoaderIcon } from '../../assets/icons/PercentLoaderIcon';
import { SearchIcon } from '../../assets/icons/SearchIcon';
import useOutsideClick from '../../hooks/useOutsideClick/useOutsideClick';
import { getUniqueKey } from '../../utils/HTMLutils';
import { getStringWithoutSpecialChar } from '../../utils/MaskUtils';
import Chip from '../chip/Chip';
import IconButton from '../iconButton/IconButton';
import Input from '../input/Input';
import './Search.scss';
const rootClassName = 'component-search';

type Record = { label: string; value: number | number };

export interface SearchProps {
  showSelected?: boolean;
  onChange?: (value: string) => void;
  onSelect?: any;
  error?: boolean;
  helperText?: string;
  label: string;
  placeholder?: string;
  disabled?: boolean;
  value: string;
  data?: Array<Record>;
  minLenghtToSearch?: number;
  clearButton?: boolean;
  loadData?: () => Promise<Array<Record>>;
}

function Search(props: SearchProps) {
  const [value, setValue] = useState<string>();
  const [selected, setSelected] = useState<string>();
  const [loading, setLoading] = useState<boolean>(false);
  const [results, setResults] = useState(props.data);

  const [id] = useState<string>(getUniqueKey());
  const [panelId] = useState<string>(getUniqueKey());

  const [showResults, setShowResults] = useState<boolean>(false);

  const wrapperRef = createRef<HTMLDivElement>();

  const handleClickOutside = (e: boolean) => {
    if (!e) setShowResults(false);
  };

  useOutsideClick([wrapperRef], handleClickOutside);

  const onEsc = (evt: KeyboardEvent) => {
    if (evt.key === 'Escape') {
      setShowResults(false);
      const el = document.querySelector(`#${id} input`) as HTMLElement;
      if (el) el.focus();
      evt.preventDefault();
      evt.stopPropagation();
    }
  };

  const handleInputChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      const { value } = e.target;
      setValue(value);

      if (value?.length >= (props.minLenghtToSearch ?? 0)) {
        if (!props.loadData) {
          setShowResults(true);
        } else {
          setShowResults(false);
          setResults([]);
        }
      }

      !!props.onChange && props.onChange(value);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [value, props.onChange]
  );

  const handleSelectItem = useCallback(
    (record: { label: string; value: number }) => {
      if (props.showSelected) {
        setValue('');
      } else {
        setValue(record.label);
      }

      setShowResults(false);
      setSelected(record.label);

      !!props.onSelect && props.onSelect(record);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  const getData = () => {
    if (typeof props.loadData === 'function') {
      setLoading(true);
      const res = props.loadData();
      if (res instanceof Promise) {
        res
          .then(() => {
            setShowResults(true);
            setSelected(undefined);
            setLoading(false);
          })
          .catch(() => {
            setLoading(false);
          });
      }
    }
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      if (
        value !== selected &&
        (value?.length ?? 1) >= (props.minLenghtToSearch ?? 0)
      ) {
        getData();
      }
    }, 1000);

    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  useEffect(() => {
    setResults(props.data);
  }, [props.data]);

  useEffect(() => {
    document.addEventListener('keydown', onEsc as any);

    if (
      props.loadData &&
      results?.length &&
      (value?.length ?? 1) >= (props.minLenghtToSearch ?? 0)
    ) {
      setLoading(false);
      setShowResults(true);
    }

    return () => {
      document.removeEventListener('keydown', onEsc as any);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [results]);

  useEffect(() => {
    setValue(props.value);

    if (!props.value) {
      setSelected(undefined);
    }

    if (props.value !== selected) {
      setSelected(undefined);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.value]);

  return (
    <div tabIndex={1} id={id} className={rootClassName} ref={wrapperRef}>
      <Input
        id={getUniqueKey()}
        value={value}
        onChange={handleInputChange}
        label={props.label}
        placeholder={props.placeholder}
        onKeyDown={(evt) => {
          if ((evt.key === 'Tab' && !evt.shiftKey) || evt.key === 'ArrowDown') {
            const query = `#${panelId} .${rootClassName}-options:nth-child(1)`;
            const optionToFocus = document.querySelector(query) as HTMLElement;
            if (optionToFocus) {
              optionToFocus.focus();
              evt.preventDefault();
            }
          }
        }}
        rightObject={
          <div className={`${rootClassName}-right-container`}>
            {props.showSelected && selected ? (
              <Chip
                label={selected}
                allClickable={false}
                w100={true}
                onClick={() => {
                  props.onSelect(undefined);
                  setValue('');
                  setSelected(undefined);
                }}
              />
            ) : undefined}
            {selected && props.onSelect && props.clearButton ? (
              <IconButton
                icon={<CloseIcon />}
                tabIndex={-1}
                onClick={() => {
                  props.onSelect(undefined);
                  setValue('');
                  setSelected(undefined);
                }}
              />
            ) : undefined}
            <IconButton
              icon={loading ? <PercentLoaderIcon indefinido /> : <SearchIcon />}
              tabIndex={-1}
              onClick={() => {
                if (props.value) {
                  if (typeof props.loadData === 'function') {
                    setShowResults(false);
                    setLoading(true);
                    const res = props.loadData();
                    if (res instanceof Promise) {
                      res
                        .then(() => {
                          setShowResults(true);
                          setLoading(false);
                        })
                        .catch(() => {
                          setLoading(false);
                        });
                    }
                  }
                }

                return undefined;
              }}
            />
          </div>
        }
        loading={loading}
        error={props.error}
        helperText={props.helperText}
        disabled={props.disabled || loading}
      />
      <CSSTransition
        in={showResults}
        timeout={300}
        classNames={'panel-fade'}
        unmountOnExit
      >
        <div
          id={panelId}
          className={`${rootClassName}-panel scroll-white`}
          style={{ width: `${wrapperRef.current?.offsetWidth}px` }}
        >
          {props.loadData
            ? results?.map((e: Record, i: number) => {
                return (
                  <div
                    key={i}
                    tabIndex={1}
                    className={`${rootClassName}-options`}
                    onClick={() => {
                      handleSelectItem(e);
                    }}
                    onKeyDown={(evt) => {
                      const child = evt.currentTarget as HTMLElement;
                      const parent = child.parentNode as HTMLElement;
                      const index = Array.prototype.indexOf.call(
                        parent.children,
                        child
                      );
                      const total = parent.childElementCount;

                      const el = document.querySelector(
                        `#${id} input`
                      ) as HTMLElement;

                      switch (evt.key) {
                        case 'Tab':
                          if (evt.shiftKey) {
                            if (index === 0) {
                              const inputPanel = document.querySelector(
                                `#${id} input`
                              ) as HTMLElement;
                              if (inputPanel) {
                                inputPanel.focus();
                                evt.preventDefault();
                              }
                            }
                          }
                          break;
                        case 'Enter':
                          handleSelectItem(e);
                          setShowResults(false);
                          if (el) el.focus();
                          evt.preventDefault();
                          break;
                        case ' ':
                          handleSelectItem(e);
                          evt.preventDefault();
                          break;
                        case 'ArrowUp':
                          if (index > 0) {
                            (parent.children[index - 1] as HTMLElement).focus();
                          } else {
                            const el = document.querySelector(
                              `#${id} input`
                            ) as HTMLElement;
                            if (el) el.focus();
                          }
                          evt.preventDefault();
                          break;
                        case 'ArrowDown':
                          if (index < total - 1) {
                            (parent.children[index + 1] as HTMLElement).focus();
                            evt.preventDefault();
                          }
                          break;
                        default:
                          break;
                      }
                    }}
                  >
                    <span>{e.label}</span>
                  </div>
                );
              })
            : results
                ?.filter((search: Record) =>
                  getStringWithoutSpecialChar(search.label)
                    .toLowerCase()
                    .match(
                      getStringWithoutSpecialChar(props.value).toLowerCase()
                    )
                )
                .map((results: Record, i: number) => {
                  return (
                    <div
                      key={i}
                      tabIndex={1}
                      className={`${rootClassName}-options`}
                      onClick={() => {
                        handleSelectItem(results);
                      }}
                      onKeyDown={(evt) => {
                        const child = evt.currentTarget as HTMLElement;
                        const parent = child.parentNode as HTMLElement;
                        const index = Array.prototype.indexOf.call(
                          parent.children,
                          child
                        );
                        const total = parent.childElementCount;

                        const el = document.querySelector(
                          `#${id} input`
                        ) as HTMLElement;

                        switch (evt.key) {
                          case 'Tab':
                            if (evt.shiftKey) {
                              if (index === 0) {
                                const inputPanel = document.querySelector(
                                  `#${id} input`
                                ) as HTMLElement;
                                if (inputPanel) {
                                  inputPanel.focus();
                                  evt.preventDefault();
                                }
                              }
                            }
                            break;
                          case 'Enter':
                            handleSelectItem(results);
                            setShowResults(false);
                            if (el) el.focus();
                            evt.preventDefault();
                            break;
                          case ' ':
                            handleSelectItem(results);
                            evt.preventDefault();
                            break;
                          case 'ArrowUp':
                            if (index > 0) {
                              (
                                parent.children[index - 1] as HTMLElement
                              ).focus();
                            } else {
                              const el = document.querySelector(
                                `#${id} input`
                              ) as HTMLElement;
                              if (el) el.focus();
                            }
                            evt.preventDefault();
                            break;
                          case 'ArrowDown':
                            if (index < total - 1) {
                              (
                                parent.children[index + 1] as HTMLElement
                              ).focus();
                              evt.preventDefault();
                            }
                            break;
                          default:
                            break;
                        }
                      }}
                    >
                      <span>{results.label}</span>
                    </div>
                  );
                })}
        </div>
      </CSSTransition>
    </div>
  );
}

Search.defaultProps = {
  minLenghtToSearch: 1,
};

export default memo(Search);
